import { useState } from "react";
import { Star, Gift, Settings, AlertCircle } from "lucide-react";
import { Button } from "@cieda/djayma-ui";
import { toast } from "sonner";
import AdminLayout from "@/components/admin/AdminLayout";
import { KPICard, AdminModal, EmptyState } from "@/components/admin/shared";
import {
  useLoyalty,
  useLoyaltyConfig,
  useAdjustLoyaltyPoints,
  useUpdateLoyaltyConfig,
} from "@/hooks/admin/useCRM";
import { formatFCFA } from "@cieda/utils";
import type { LoyaltyMember, LoyaltyTier } from "@/lib/admin-api/crm";

// ---------------------------------------------------------------------------
// Tier config
// ---------------------------------------------------------------------------

const TIER_LABELS: Record<LoyaltyTier, string> = {
  bronze: "Bronze",
  silver: "Argent",
  gold: "Or",
};

const TIER_STYLES: Record<LoyaltyTier, string> = {
  bronze: "bg-amber-50 text-amber-700",
  silver: "bg-stone-100 text-stone-600",
  gold: "bg-yellow-50 text-yellow-700",
};

const TIER_RANGES = [
  { tier: "bronze" as LoyaltyTier, label: "Bronze", range: "0 – 999 pts" },
  { tier: "silver" as LoyaltyTier, label: "Argent", range: "1 000 – 4 999 pts" },
  { tier: "gold" as LoyaltyTier, label: "Or", range: "5 000 pts et plus" },
];

type Tab = "dashboard" | "members" | "config";

// ---------------------------------------------------------------------------
// Tab nav
// ---------------------------------------------------------------------------

function TabNav({ active, onChange }: { active: Tab; onChange: (t: Tab) => void }) {
  const tabs: Array<{ key: Tab; label: string }> = [
    { key: "dashboard", label: "Tableau de bord" },
    { key: "members", label: "Membres" },
    { key: "config", label: "Configuration" },
  ];
  return (
    <div className="flex gap-1 border-b border-stone-200">
      {tabs.map((tab) => (
        <button
          key={tab.key}
          onClick={() => onChange(tab.key)}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
            active === tab.key
              ? "border-amber-500 text-amber-700"
              : "border-transparent text-stone-500 hover:text-stone-800"
          }`}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Adjust points modal
// ---------------------------------------------------------------------------

function AdjustModal({
  member,
  open,
  onClose,
}: {
  member: LoyaltyMember;
  open: boolean;
  onClose: () => void;
}) {
  const [points, setPoints] = useState("");
  const [reason, setReason] = useState("");
  const adjust = useAdjustLoyaltyPoints();

  const handleSubmit = () => {
    const delta = parseInt(points, 10);
    if (isNaN(delta) || delta === 0) {
      toast.error("Veuillez saisir un nombre de points valide.");
      return;
    }
    if (!reason.trim()) {
      toast.error("La raison est obligatoire.");
      return;
    }
    adjust.mutate(
      { customer_id: member.customer_id, points_delta: delta, reason },
      {
        onSuccess: () => {
          toast.success("Points ajustés avec succès.");
          setPoints("");
          setReason("");
          onClose();
        },
        onError: () => toast.error("Erreur lors de l'ajustement des points."),
      },
    );
  };

  return (
    <AdminModal
      open={open}
      onClose={onClose}
      title={`Ajuster les points — ${member.full_name}`}
      size="sm"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={handleSubmit} disabled={adjust.isPending}>
            {adjust.isPending ? "En cours..." : "Confirmer"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="rounded-lg bg-stone-50 px-4 py-3 text-sm">
          <span className="text-stone-500">Solde actuel : </span>
          <span className="font-semibold text-stone-800">
            {member.points_balance.toLocaleString("fr-SN")} pts
          </span>
        </div>
        <div className="space-y-1">
          <label className="text-sm font-medium text-stone-700">
            Ajustement (+ pour ajouter, − pour retirer)
          </label>
          <input
            type="number"
            value={points}
            onChange={(e) => setPoints(e.target.value)}
            placeholder="ex : 100 ou -50"
            className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-300"
          />
        </div>
        <div className="space-y-1">
          <label className="text-sm font-medium text-stone-700">
            Raison <span className="text-red-500">*</span>
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="Ex : Correction suite à litige commande #1234"
            className="w-full resize-none rounded-lg border border-stone-200 px-3 py-2 text-sm placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-300"
          />
        </div>
      </div>
    </AdminModal>
  );
}

// ---------------------------------------------------------------------------
// Dashboard tab
// ---------------------------------------------------------------------------

function DashboardTab() {
  const { data, isLoading, isError } = useLoyalty();

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 animate-pulse">
        {[...Array(3)].map((_, i) => <div key={i} className="h-28 rounded-lg bg-stone-100" />)}
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
        <AlertCircle className="h-4 w-4 shrink-0" />
        Impossible de charger les données fidélité.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <KPICard
          label="Points en circulation"
          value={data.total_points_in_circulation}
          format="number"
          icon={<Gift className="h-4 w-4" />}
        />
        <KPICard
          label="Membres du programme"
          value={data.total_members}
          format="number"
          icon={<Star className="h-4 w-4" />}
        />
        <KPICard
          label="Valeur FCFA des points"
          value={data.points_fcfa_value}
          format="currency"
        />
      </div>

      <div className="rounded-xl border border-stone-200 bg-white p-5 space-y-3">
        <h3 className="text-sm font-semibold text-stone-700">Tiers du programme</h3>
        <div className="flex flex-wrap gap-3">
          {TIER_RANGES.map((t) => (
            <div key={t.tier} className="flex items-center gap-2 rounded-lg bg-stone-50 px-3 py-2">
              <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${TIER_STYLES[t.tier]}`}>
                {t.label}
              </span>
              <span className="text-sm text-stone-500">{t.range}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Members tab
// ---------------------------------------------------------------------------

function MembersTab() {
  const { data, isLoading } = useLoyalty();
  const [adjustTarget, setAdjustTarget] = useState<LoyaltyMember | null>(null);
  const members = data?.members ?? [];

  if (isLoading) {
    return (
      <div className="space-y-3 animate-pulse">
        {[...Array(6)].map((_, i) => <div key={i} className="h-12 rounded bg-stone-100" />)}
      </div>
    );
  }

  if (members.length === 0) {
    return (
      <EmptyState
        icon={<Star className="h-6 w-6" />}
        title="Aucun membre du programme"
        description="Les clients rejoindront automatiquement le programme lors de leur première commande."
      />
    );
  }

  return (
    <>
      <div className="overflow-hidden rounded-xl border border-stone-200 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-stone-100 bg-stone-50">
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Client</th>
                <th className="hidden sm:table-cell px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Tier</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-stone-500">Points</th>
                <th className="hidden md:table-cell px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Dernière commande</th>
                <th className="w-28 px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {members.map((m) => (
                <tr key={m.id} className="hover:bg-stone-50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber-100 text-xs font-medium text-amber-800">
                        {m.full_name.charAt(0)}
                      </div>
                      <div>
                        <p className="font-medium text-stone-900">{m.full_name}</p>
                        <p className="text-xs text-stone-400">{m.phone}</p>
                      </div>
                    </div>
                  </td>
                  <td className="hidden sm:table-cell px-4 py-3">
                    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${TIER_STYLES[m.tier]}`}>
                      {TIER_LABELS[m.tier]}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums font-semibold text-stone-800">
                    {m.points_balance.toLocaleString("fr-SN")}
                  </td>
                  <td className="hidden md:table-cell px-4 py-3 text-stone-500">
                    {m.last_order_date ?? "—"}
                  </td>
                  <td className="px-4 py-3">
                    <Button variant="outline" size="sm" onClick={() => setAdjustTarget(m)}>
                      Ajuster
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {adjustTarget && (
        <AdjustModal member={adjustTarget} open={true} onClose={() => setAdjustTarget(null)} />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Config tab
// ---------------------------------------------------------------------------

function ConfigTab() {
  const { data: config, isLoading } = useLoyaltyConfig();
  const updateConfig = useUpdateLoyaltyConfig();
  const [earnRate, setEarnRate] = useState("");
  const [burnRate, setBurnRate] = useState("");

  const handleSave = () => {
    const earn = parseFloat(earnRate);
    const burn = parseFloat(burnRate);
    if (isNaN(earn) || isNaN(burn)) {
      toast.error("Valeurs invalides.");
      return;
    }
    updateConfig.mutate(
      { earn_rate_fcfa_per_point: earn, burn_rate_points_per_fcfa: burn },
      {
        onSuccess: () => toast.success("Configuration mise à jour."),
        onError: () => toast.error("Erreur lors de la mise à jour."),
      },
    );
  };

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-4">
        {[...Array(4)].map((_, i) => <div key={i} className="h-12 rounded bg-stone-100" />)}
      </div>
    );
  }

  return (
    <div className="max-w-lg space-y-6">
      <div className="rounded-xl border border-stone-200 bg-white p-5 space-y-5">
        <h3 className="text-sm font-semibold text-stone-700">Règles de points</h3>
        <div className="space-y-1">
          <label className="text-sm font-medium text-stone-700">
            Taux de gain : 1 pt pour X FCFA dépensés
          </label>
          <input
            type="number"
            defaultValue={config?.earn_rate_fcfa_per_point}
            onChange={(e) => setEarnRate(e.target.value)}
            placeholder="ex : 500"
            className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
          />
          <p className="text-xs text-stone-400">Pour X FCFA dépensés → 1 point gagné</p>
        </div>
        <div className="space-y-1">
          <label className="text-sm font-medium text-stone-700">
            Taux de burn : 100 pts = X FCFA de réduction
          </label>
          <input
            type="number"
            defaultValue={config?.burn_rate_points_per_fcfa}
            onChange={(e) => setBurnRate(e.target.value)}
            placeholder="ex : 1000"
            className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
          />
          <p className="text-xs text-stone-400">100 points = valeur FCFA de réduction renseignée</p>
        </div>
      </div>

      <div className="rounded-xl border border-stone-200 bg-white p-5 space-y-3">
        <h3 className="text-sm font-semibold text-stone-700">Seuils des tiers (lecture seule)</h3>
        <div className="space-y-2">
          {TIER_RANGES.map((t) => (
            <div key={t.tier} className="flex items-center justify-between rounded-lg bg-stone-50 px-3 py-2">
              <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${TIER_STYLES[t.tier]}`}>
                {t.label}
              </span>
              <span className="text-sm text-stone-500">{t.range}</span>
            </div>
          ))}
        </div>
        <p className="text-xs text-stone-400">
          Contactez l'équipe technique pour modifier les seuils des tiers.
        </p>
      </div>

      <Button onClick={handleSave} disabled={updateConfig.isPending}>
        {updateConfig.isPending ? "Enregistrement..." : "Enregistrer les taux"}
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function Loyalty() {
  const [tab, setTab] = useState<Tab>("dashboard");

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
            <Star className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display text-xl font-semibold text-stone-900">
              Programme de fidélité
            </h1>
            <p className="text-sm text-stone-500">
              Gestion des points, des tiers et de la configuration
            </p>
          </div>
        </div>

        <TabNav active={tab} onChange={setTab} />

        {tab === "dashboard" && <DashboardTab />}
        {tab === "members" && <MembersTab />}
        {tab === "config" && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm text-stone-500">
              <Settings className="h-4 w-4" />
              <span>Configuration du programme de fidélité</span>
            </div>
            <ConfigTab />
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
